import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  Share2, Copy, Check, Linkedin, Twitter, Facebook, Mail, 
  MessageCircle, Trophy, TrendingUp, Users, Sparkles 
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShareModal({ isOpen, onClose, event }) {
  const [step, setStep] = useState('email'); // email, share, success
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [shareContent, setShareContent] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen && event?.id) {
      fetchShareContent();
      // Check if user already has referral data
      const savedEmail = localStorage.getItem('boost_email');
      const savedName = localStorage.getItem('boost_name');
      if (savedEmail) {
        setEmail(savedEmail);
        setName(savedName || '');
      }
    }
  }, [isOpen, event?.id]);

  const fetchShareContent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/boost/share-content/${event.id}`);
      setShareContent(response.data.share_content);
    } catch (error) {
      console.error('Error fetching share content:', error);
    }
  };

  const generateReferralLink = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/boost/generate-link`, {
        event_id: event.id,
        referrer_email: email,
        referrer_name: name
      });

      if (response.data.success) {
        setReferralData(response.data);
        localStorage.setItem('boost_email', email);
        localStorage.setItem('boost_name', name);
        setStep('share');
        toast.success('Your referral link is ready!');
      }
    } catch (error) {
      toast.error('Failed to generate referral link');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (referralData?.referral_url) {
      await navigator.clipboard.writeText(referralData.referral_url);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareToSocial = (platform) => {
    if (!referralData?.referral_url || !shareContent) return;

    const url = referralData.referral_url;
    let shareUrl = '';

    switch (platform) {
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareContent.twitter)}&url=${encodeURIComponent(url)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(shareContent.facebook)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(shareContent.whatsapp + ' ' + url)}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${encodeURIComponent(shareContent.email.subject)}&body=${encodeURIComponent(shareContent.email.body + '\n\n' + url)}`;
        break;
      default:
        return;
    }

    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const handleClose = () => {
    setStep('email');
    onClose();
  };

  if (!event) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="p-2 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span>Boost This Event</span>
          </DialogTitle>
        </DialogHeader>

        {step === 'email' && (
          <div className="space-y-6 py-4">
            {/* Value Proposition */}
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-100">
              <h3 className="font-semibold text-gray-900 mb-3">Share & Earn Rewards!</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-orange-500" />
                  <span>Earn badges and climb the leaderboard</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-orange-500" />
                  <span>Help your network discover great events</span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-orange-500" />
                  <span>Track your referral performance</span>
                </div>
              </div>
            </div>

            {/* Email Input */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Your Email</label>
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Your Name (optional)</label>
                <Input
                  type="text"
                  placeholder="John Smith"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>

            <Button
              onClick={generateReferralLink}
              disabled={loading || !email}
              className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
            >
              {loading ? 'Generating...' : 'Get My Referral Link'}
            </Button>

            <p className="text-xs text-gray-500 text-center">
              By continuing, you agree to receive updates about your referral performance.
            </p>
          </div>
        )}

        {step === 'share' && referralData && (
          <div className="space-y-6 py-4">
            {/* Success Message */}
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Your Link is Ready!</h3>
              <p className="text-sm text-gray-600 mt-1">Share it to earn rewards</p>
            </div>

            {/* Referral Link Box */}
            <div className="bg-gray-50 rounded-lg p-4">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Your Referral Link</label>
              <div className="flex gap-2 mt-2">
                <Input
                  value={referralData.referral_url}
                  readOnly
                  className="bg-white text-sm"
                />
                <Button
                  variant="outline"
                  onClick={copyToClipboard}
                  className="shrink-0"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* Social Share Buttons */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-3 block">Share on Social Media</label>
              <div className="grid grid-cols-5 gap-2">
                <button
                  onClick={() => shareToSocial('linkedin')}
                  className="flex flex-col items-center gap-1 p-3 rounded-lg bg-[#0077B5] text-white hover:bg-[#006399] transition-colors"
                >
                  <Linkedin className="w-5 h-5" />
                  <span className="text-xs">LinkedIn</span>
                </button>
                <button
                  onClick={() => shareToSocial('twitter')}
                  className="flex flex-col items-center gap-1 p-3 rounded-lg bg-black text-white hover:bg-gray-800 transition-colors"
                >
                  <Twitter className="w-5 h-5" />
                  <span className="text-xs">X</span>
                </button>
                <button
                  onClick={() => shareToSocial('facebook')}
                  className="flex flex-col items-center gap-1 p-3 rounded-lg bg-[#1877F2] text-white hover:bg-[#166FE5] transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                  <span className="text-xs">Facebook</span>
                </button>
                <button
                  onClick={() => shareToSocial('whatsapp')}
                  className="flex flex-col items-center gap-1 p-3 rounded-lg bg-[#25D366] text-white hover:bg-[#20BD5A] transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span className="text-xs">WhatsApp</span>
                </button>
                <button
                  onClick={() => shareToSocial('email')}
                  className="flex flex-col items-center gap-1 p-3 rounded-lg bg-gray-600 text-white hover:bg-gray-700 transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  <span className="text-xs">Email</span>
                </button>
              </div>
            </div>

            {/* View Dashboard Link */}
            <div className="text-center pt-2">
              <a
                href={`/boost/dashboard?email=${encodeURIComponent(email)}`}
                className="text-sm text-orange-600 hover:text-orange-700 font-medium"
              >
                View My Referral Dashboard →
              </a>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Compact Share Button for Event Pages
export function BoostShareButton({ event, variant = 'default' }) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (variant === 'compact') {
    return (
      <>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg hover:from-orange-600 hover:to-amber-600 transition-all shadow-md hover:shadow-lg"
        >
          <Share2 className="w-4 h-4" />
          <span className="font-medium">{t('common.shareEarn')}</span>
        </button>
        <ShareModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} event={event} />
      </>
    );
  }

  return (
    <>
      <Button
        onClick={() => setIsModalOpen(true)}
        className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
      >
        <Share2 className="w-4 h-4 mr-2" />
        {t('form.shareEarn')}
      </Button>
      <ShareModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} event={event} />
    </>
  );
}